import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { SUPPORTED_LANGUAGES, LANGUAGE_FLAGS, changeLanguage, type SupportedLanguage, getDirection } from '../i18n';
import { authService } from '../services/authService';
import { useApp } from '../contexts/AppContext';

interface LanguageSelectorProps {
  className?: string;
  variant?: 'dropdown' | 'inline' | 'flags';
  showLabel?: boolean;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  className = '',
  variant = 'dropdown',
  showLabel = true
}) => {
  const { t, i18n } = useTranslation();
  const { state, updateUser } = useApp();
  const [isOpen, setIsOpen] = useState(false);
  const [isChanging, setIsChanging] = useState(false);

  const currentLanguage = i18n.language as SupportedLanguage;
  const isRTL = getDirection(currentLanguage) === 'rtl';

  const handleLanguageChange = async (language: SupportedLanguage) => {
    if (language === currentLanguage) {
      setIsOpen(false);
      return;
    }

    try {
      setIsChanging(true);
      
      // Change the frontend language first
      await changeLanguage(language);
      
      // Update the backend if user is authenticated
      if (state.isAuthenticated && authService.isAuthenticated()) {
        try {
          await authService.updateLocale({ locale: language });
          
          // Update the user in the app context with new locale
          if (state.user) {
            const updatedUser = { ...state.user, locale: language };
            updateUser(updatedUser);
          }
        } catch (error) {
          console.error('Failed to update locale on backend:', error);
          // Don't throw error here - allow frontend language change to persist
          // even if backend update fails
        }
      }
      
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      setIsChanging(false);
    }
  };

  if (variant === 'flags') {
    return (
      <div className={`language-selector-flags ${className}`}>
        <div className="language-flags-container">
          {Object.entries(SUPPORTED_LANGUAGES).map(([code, name]) => {
            const langCode = code as SupportedLanguage;
            const isActive = langCode === currentLanguage;
            const flag = LANGUAGE_FLAGS[langCode];
            
            return (
              <button
                key={code}
                onClick={() => handleLanguageChange(langCode)}
                disabled={isChanging}
                className={`language-flag-button ${isActive ? 'language-flag-active' : ''}`}
                data-language={code}
                aria-label={`Switch to ${name}`}
                title={name}
              >
                <span className="flag-emoji">{flag}</span>
                {isActive && <div className="flag-active-indicator" />}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <div className={`language-selector-inline ${className}`}>
        {showLabel && (
          <label className="language-label-modern">
            <Globe size={16} />
            {t('navigation.language')}
          </label>
        )}
        <div className="language-options-inline">
          {Object.entries(SUPPORTED_LANGUAGES).map(([code, name]) => {
            const langCode = code as SupportedLanguage;
            const isActive = langCode === currentLanguage;
            
            return (
              <button
                key={code}
                onClick={() => handleLanguageChange(langCode)}
                disabled={isChanging}
                className={`language-option-inline ${isActive ? 'language-option-active' : ''}`}
                data-language={code}
              >
                {isActive && <Check size={14} />}
                <span className="language-name">{name}</span>
                {isActive && <span className="language-current-indicator">•</span>}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className={`language-selector-dropdown ${isRTL ? 'rtl' : 'ltr'} ${className}`}>
      <button
        className="language-trigger-modern"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isChanging}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={t('navigation.language')}
      >
        <div className="language-trigger-content">
          <Globe size={16} />
          {showLabel && (
            <span className="language-current-text">
              {SUPPORTED_LANGUAGES[currentLanguage]}
            </span>
          )}
          <ChevronDown 
            size={14} 
            className={`language-chevron ${isOpen ? 'language-chevron-open' : ''}`}
          />
        </div>
      </button>

      {isOpen && (
        <>
          <div 
            className="language-backdrop" 
            onClick={() => setIsOpen(false)}
          />
          <div className="language-dropdown-menu" role="listbox">
            {Object.entries(SUPPORTED_LANGUAGES).map(([code, name]) => {
              const langCode = code as SupportedLanguage;
              const isActive = langCode === currentLanguage;
              const direction = getDirection(langCode);
              
              return (
                <button
                  key={code}
                  onClick={() => handleLanguageChange(langCode)}
                  disabled={isChanging}
                  className={`language-option-modern ${isActive ? 'language-option-active' : ''}`}
                  role="option"
                  aria-selected={isActive}
                  data-language={code}
                  data-direction={direction}
                >
                  <span className="language-name" dir={direction}>
                    {name}
                  </span>
                  {isActive && (
                    <Check size={16} className="language-check" />
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};